/**
 * Identidade visual + tokens — Avaliação NBR Enterprise - Automatic (RQ-096).
 */
export const AUTOMATIC_BRAND = Object.freeze({
  productName: "Avaliação NBR Enterprise - Automatic",
  motorLabel: "Automatic AI",
  runnerLabel: "Challenger V1 Core",
  runnerPlugin: "lab.challenger_v1_core.adapter:ChallengerV1CoreAdapter",
  modelVersion: "challenger_v1_core",
  contractVersion: "valuation-automatic-ux-ptam-premium-v1"
});

export const AUTOMATIC_THEME = Object.freeze({
  primary: "#0B3D5C",
  accent: "#1F9E8A",
  accentSoft: "#E6F7F4",
  surface: "#F4F7FA",
  ink: "#0F172A",
  muted: "#64748B",
  warning: "#B45309",
  cardRadius: 14,
  fontDisplay: '"Source Serif 4", "Libre Baskerville", Georgia, serif',
  fontBody: '"IBM Plex Sans", "Segoe UI", sans-serif'
});

export const TIMELINE_STEPS = Object.freeze([
  { id: "dados", label: "Dados", matchSteps: [1] },
  { id: "endereco", label: "Endereço", matchSteps: [2] },
  { id: "localizacao", label: "Localização", matchSteps: [3] },
  { id: "comparaveis", label: "Comparáveis", matchSteps: [4, 5] },
  { id: "regressao", label: "Regressão", matchSteps: [6] },
  { id: "resultado", label: "Resultado", matchSteps: [7] },
  { id: "relatorios", label: "Relatórios", matchSteps: [8] }
]);

export const POI_UX_CATALOG = Object.freeze([
  { key: "metro", emoji: "🚇", label: "Trilho mais próximo" },
  { key: "parque", emoji: "🌳", label: "Parque (mais próximo dos 2)" }
]);

export const ONBOARDING_STORAGE_KEY = "avm_automatic_onboarding_v2_dismissed";

export const ONBOARDING_STEPS = Object.freeze([
  {
    title: "Bem-vindo · 8 etapas",
    body:
      "Você vai: identificar o pedido → endereço e dados do imóvel → mapa → vizinhos e Avaliar → contexto → política 0/5/10% → resultado e gráficos → relatórios PDF. Use Continuar / Voltar e as bolinhas numeradas."
  },
  {
    title: "Mapa, raio e Avaliar",
    body:
      "No mapa: arraste e dê zoom. Na Etapa 4 ajuste o raio (metros), busque vizinhos, revise a amostra e clique em Avaliar — aí o modelo calcula o valor."
  },
  {
    title: "Painel no topo",
    body:
      "Depois do cálculo, o Painel mostra valor, VU, intervalo admissível (±15%), R² e mais. Acompanhe sempre ao mudar a política ou o arbítrio."
  },
  {
    title: "Política e arbítrio",
    body:
      "Etapa 6: escolha 0%, 5% ou 10% (desconto comercial; default 0%). Etapa 7: se precisar, aplique arbítrio do avaliador com justificativa. São ajustes depois do motor."
  },
  {
    title: "Gráficos e PDFs",
    body:
      "Na Etapa 7 role os gráficos (P10–P90, resíduos, ajuste). Na Etapa 8 gere os PDFs. Ajuda e Reiniciar tutorial ficam na barra superior."
  }
]);

/** Índice de Qualidade Urbana — apoio visual (não entra na regressão). */
export function computeUrbanQualityIndex({ ibge, poi } = {}) {
  const percentil = Number(ibge?.percentil_renda_sp);
  const rendaScore = Number.isFinite(percentil)
    ? Math.max(0, Math.min(100, percentil))
    : 45;
  const counts = poi?.counts || {};
  const infraHits = ["escola", "hospital", "metro", "parque", "shopping", "supermercado", "universidade"]
    .map((k) => Number(counts[k] || 0))
    .reduce((a, b) => a + (b > 0 ? 1 : 0), 0);
  const infraScore = (infraHits / 7) * 100;
  const distKeys = [
    "dist_metro_m",
    "dist_escola_m",
    "dist_parque_m",
    "dist_hospital_m",
    "dist_shopping_m",
    "dist_supermercado_m"
  ];
  const dists = distKeys.map((k) => Number(poi?.[k])).filter((d) => Number.isFinite(d) && d >= 0);
  const nearScore =
    dists.length === 0
      ? 40
      : Math.max(
          0,
          Math.min(
            100,
            100 - Math.min(100, (dists.reduce((a, b) => a + b, 0) / dists.length) / 20)
          )
        );
  const iqu = Math.round(rendaScore * 0.45 + infraScore * 0.3 + nearScore * 0.25);
  let classe = "Média";
  if (iqu >= 75) classe = "Alta";
  else if (iqu >= 55) classe = "Média-alta";
  else if (iqu < 35) classe = "Baixa";
  return {
    iqu,
    classe,
    components: { rendaScore, infraScore, nearScore },
    note:
      "IQU é experimental e apenas visual/explicativo. Não entra como variável da regressão nesta versão."
  };
}

export function interpretIbge(ibge = {}) {
  const pct = Number(ibge.percentil_renda_sp ?? ibge.percentil);
  const renda = Number(
    ibge.renda_responsavel_media ??
      ibge.renda_media ??
      ibge.renda_domiciliar_media ??
      ibge.renda_per_capita_estimada
  );
  let classe = "Não classificado";
  let score = null;
  let plain =
    "Os dados do IBGE são utilizados para caracterizar o contexto socioeconômico da localização. Nesta versão, não integram diretamente o cálculo estatístico do valor.";
  const hasAny =
    Number.isFinite(pct) ||
    Number.isFinite(renda) ||
    ibge.cd_setor ||
    ibge.nm_bairro ||
    ibge.fonte;
  if (!hasAny) {
    plain = "IBGE indisponível para este ponto (sem setor/renda no payload).";
  } else if (Number.isFinite(pct)) {
    score = Math.round(pct);
    if (pct >= 80) classe = "Renda elevada (top 20% SP)";
    else if (pct >= 60) classe = "Renda acima da mediana";
    else if (pct >= 40) classe = "Renda próxima da mediana";
    else if (pct >= 20) classe = "Renda abaixo da mediana";
    else classe = "Renda baixa (bottom 20% SP)";
    plain = `O setor censitário situa-se aproximadamente no percentil ${pct.toLocaleString("pt-BR")} da renda em São Paulo${
      Number.isFinite(renda)
        ? `, com renda média do responsável de ${renda.toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL"
          })}`
        : ""
    }. Os dados do IBGE caracterizam o contexto socioeconômico da localização; nesta versão, não integram diretamente o cálculo estatístico do valor.`;
  } else if (Number.isFinite(renda)) {
    plain = `Renda média do responsável ${renda.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL"
    })} no setor de referência${ibge.nm_bairro ? ` (${ibge.nm_bairro})` : ""}. Contexto socioeconômico — não entra como variável X do modelo Automatic nesta versão.`;
  }
  return {
    classe,
    score,
    plain,
    percentil: Number.isFinite(pct) ? pct : null,
    renda: Number.isFinite(renda) ? renda : null,
    territorio: ibge.nm_bairro || ibge.nm_mun || ibge.cd_setor || null,
    fonte: ibge.fonte || (hasAny ? "IBGE/censo + geocode setor" : null)
  };
}

/**
 * Agrupa contribuições do Challenger em linguagem simples (R$ e %).
 * Sem alterar o motor — só apresentação.
 */
export function groupExplainableContributions(result = {}, subject = {}) {
  const contribs = result.feature_contributions || result.explainability?.feature_contributions || [];
  const total = Number(result.valor_total) || Number(result.valor_central) || 0;
  const vu = Number(result.vu_ajustado) || 0;
  const area = Number(subject.areaPrivativa || subject.area_privativa) || 0;

  const groups = {
    fisicas: { label: "Características físicas", amount: 0, keys: [] },
    localizacao: { label: "Localização", amount: 0, keys: [] },
    comparaveis: { label: "Comparáveis / amostra", amount: 0, keys: [] },
    ibge: { label: "IBGE / renda", amount: 0, keys: [] },
    pois: { label: "POIs / entorno", amount: 0, keys: [] },
    oferta: { label: "Oferta / elasticidade", amount: 0, keys: [] },
    outros: { label: "Outros fatores", amount: 0, keys: [] }
  };

  const classify = (name) => {
    const n = String(name || "").toLowerCase();
    if (/area|quarto|banheiro|vaga|suite|andar|amenit|condomin/.test(n)) return "fisicas";
    if (/dist|lat|lng|bairro|local/.test(n)) return "localizacao";
    if (/renda|ibge|setor|log_renda/.test(n)) return "ibge";
    if (/poi|escola|metro|hospital|parque|shopping/.test(n)) return "pois";
    if (/offer|oferta|elastic/.test(n)) return "oferta";
    if (/comp|similar|intercept|const/.test(n)) return "comparaveis";
    return "outros";
  };

  let listed = [];
  if (Array.isArray(contribs) && contribs.length) {
    listed = contribs.map((c) => {
      const key = c.feature || c.name || c.variable || "fator";
      const raw = Number(c.contribution_brl ?? c.contribution ?? c.value ?? c.delta_vu ?? 0);
      // se contribuição em VU, escala para total quando possível
      const amount =
        Math.abs(raw) < 500 && area > 0 && vu > 0 ? raw * area : raw;
      return { key, amount, group: classify(key) };
    });
  } else {
    // fallback transparente quando o motor não devolveu contribuições detalhadas
    const offerPct = Number(result.offer_factor?.percent || 5);
    const elPct = Number((result.elasticity?.rate || 0) * 100);
    listed = [
      { key: "intercepto / amostra", amount: total * 0.55, group: "comparaveis" },
      { key: "atributos físicos", amount: total * 0.25, group: "fisicas" },
      { key: "renda IBGE", amount: total * 0.1, group: "ibge" },
      { key: "contexto territorial", amount: total * 0.05, group: "localizacao" },
      {
        key: `fator oferta (${offerPct}%)`,
        amount: total * (-offerPct / 100) * 0.5,
        group: "oferta"
      },
      {
        key: `elasticidade (${elPct.toFixed(0)}%)`,
        amount: total * (-elPct / 100) * 0.3,
        group: "oferta"
      }
    ];
  }

  listed.forEach((row) => {
    const g = groups[row.group] || groups.outros;
    g.amount += row.amount;
    g.keys.push(row.key);
  });

  const absSum = Object.values(groups).reduce((s, g) => s + Math.abs(g.amount), 0) || 1;
  const rows = Object.values(groups)
    .filter((g) => Math.abs(g.amount) > 0.01 || g.keys.length)
    .map((g) => ({
      ...g,
      pct: (g.amount / absSum) * 100
    }));

  return {
    rows,
    disclaimer:
      "Influência estatística local estimada pelo Challenger V1 Core. Não implica causalidade. POIs não valorizam automaticamente o imóvel (UKO).",
    approximated: !(Array.isArray(contribs) && contribs.length)
  };
}
